/**
 * HQC77 "Customer Voice" tab — triage queue with SLA aging indicators.
 * Clicking a row opens TicketDetailDrawer for full timeline + reply.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Loader2, Star, AlertTriangle, Flame, Clock, CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import TicketDetailDrawer from "@/components/admin/TicketDetailDrawer";
import { friendlyError } from "@/lib/friendlyError";

const STATUS_META = {
  open:          { label: "Open",          chip: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  in_progress:   { label: "In Progress",   chip: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  awaiting_chef: { label: "Awaiting Chef", chip: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  resolved:      { label: "Resolved",      chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  closed:        { label: "Closed",        chip: "bg-slate-700 text-slate-300 border-slate-600" },
};

const CATEGORY_LABEL = {
  bug_or_complaint:  "Bug",
  feature_request:   "Feature",
  billing_issue:     "Billing",
  translation_issue: "Translation",
  general_feedback:  "Feedback",
};

const AGING_META = {
  overdue:     { chip: "bg-rose-500 text-white",       icon: Flame,          label: "Overdue" },
  approaching: { chip: "bg-amber-500 text-slate-950",  icon: AlertTriangle,  label: "Aging" },
  fresh:       { chip: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40", icon: Clock, label: "Fresh" },
  done:        { chip: "bg-slate-800 text-slate-500 border border-slate-700",            icon: CheckCircle2, label: "Done" },
};

function AgingChip({ band, hoursOpen }) {
  const meta = AGING_META[band] || AGING_META.fresh;
  const Icon = meta.icon;
  return (
    <span
      className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.65rem] font-bold tracking-wide", meta.chip)}
      data-testid={`hq-ticket-aging-${band}`}
      title={hoursOpen != null ? `${hoursOpen.toFixed(1)}h open` : meta.label}
    >
      <Icon className="h-2.5 w-2.5" />
      {hoursOpen != null ? `${Math.round(hoursOpen)}h` : meta.label}
    </span>
  );
}

export default function CustomerVoiceTab({ me, registerRefresh }) {
  const [tickets, setTickets] = useState([]);
  const [agingCounts, setAgingCounts] = useState({ overdue: 0, approaching: 0, fresh: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openTicketId, setOpenTicketId] = useState(null);

  const authHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${session?.access_token || ""}` };
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const h = await authHeaders();
      const params = new URLSearchParams({ page: 1, page_size: 100 });
      if (q) params.set("q", q);
      if (statusFilter) params.set("status", statusFilter);
      const res = await axios.get(`${API}/admin/tickets?${params}`, { headers: h });
      setTickets(res.data.tickets || []);
      setAgingCounts(res.data.aging_counts || { overdue: 0, approaching: 0, fresh: 0 });
    } catch (err) {
      toast.error(friendlyError(err, "Failed to load tickets"));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, q, statusFilter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => {
    if (registerRefresh) registerRefresh(() => fetchTickets);
  }, [registerRefresh, fetchTickets]);

  return (
    <div className="space-y-4" data-testid="hq-tab-customer-voice">
      {/* Counts header */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3">
          <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-rose-300 font-mono">
            <Flame className="h-3 w-3" />Overdue (&gt;48h)
          </div>
          <div className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="hq-aging-count-overdue">{agingCounts.overdue}</div>
        </div>
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-amber-300 font-mono">
            <AlertTriangle className="h-3 w-3" />Approaching (24-48h)
          </div>
          <div className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="hq-aging-count-approaching">{agingCounts.approaching}</div>
        </div>
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
          <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-emerald-300 font-mono">
            <Clock className="h-3 w-3" />Fresh (&lt;24h)
          </div>
          <div className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="hq-aging-count-fresh">{agingCounts.fresh}</div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject or TKT-…"
            className="pl-9 bg-slate-900 border-slate-700 text-white"
            data-testid="hq-tickets-search"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {["", "open", "in_progress", "awaiting_chef", "resolved", "closed"].map((s) => (
            <button
              key={s || "all"} type="button"
              onClick={() => setStatusFilter(s)}
              className={cn(
                "px-3 py-1.5 rounded text-xs font-semibold border transition-colors",
                statusFilter === s
                  ? "bg-emerald-500 text-slate-950 border-emerald-400"
                  : "bg-slate-900 text-slate-400 border-slate-700 hover:border-emerald-500/50"
              )}
              data-testid={`hq-tickets-status-filter-${s || "all"}`}
            >
              {s ? STATUS_META[s]?.label || s : "All"}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-500 ml-1 font-mono">{tickets.length} shown</span>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/50 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 border-b border-slate-800">
            <tr className="text-left text-[0.65rem] uppercase tracking-[0.18em] text-slate-500">
              <th className="px-4 py-2.5 font-semibold">Ref</th>
              <th className="px-4 py-2.5 font-semibold">Subject</th>
              <th className="px-4 py-2.5 font-semibold">Chef</th>
              <th className="px-4 py-2.5 font-semibold">Category</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold">Rating</th>
              <th className="px-4 py-2.5 font-semibold">SLA</th>
            </tr>
          </thead>
          <tbody data-testid="hq-tickets-tbody">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : tickets.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">No tickets match.</td></tr>
            ) : tickets.map((t) => {
              const status = STATUS_META[t.status] || STATUS_META.open;
              return (
                <tr key={t.id}
                    onClick={() => setOpenTicketId(t.id)}
                    className="border-b border-slate-800 last:border-0 hover:bg-slate-900/80 cursor-pointer"
                    data-testid={`hq-tickets-row-${t.id}`}>
                  <td className="px-4 py-2.5 text-xs text-amber-300 font-mono font-bold">{t.reference}</td>
                  <td className="px-4 py-2.5 text-xs text-white max-w-xs truncate">{t.subject}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">{t.chef_email || t.user_id?.slice(0,8) + "…"}</td>
                  <td className="px-4 py-2.5 text-[0.7rem] text-slate-300">{CATEGORY_LABEL[t.category] || t.category}</td>
                  <td className="px-4 py-2.5">
                    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold border", status.chip)}>
                      {status.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {t.rating ? (
                      <span className="inline-flex items-center gap-0.5 text-amber-400 text-xs">
                        <Star className="h-3 w-3 fill-current" />{t.rating}
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <AgingChip band={t.aging_band} hoursOpen={t.hours_open} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <TicketDetailDrawer
        ticketId={openTicketId}
        me={me}
        onClose={() => { setOpenTicketId(null); fetchTickets(); }}
      />
    </div>
  );
}
